<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EnterpriseAccountAPI from 'dashboard/api/enterprise/account';

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();

const STEP_SELECT = 'select';
const STEP_CONFIRM = 'confirm';

const dialogRef = ref(null);
const selectedProductId = ref(null);
const isLoading = ref(false);
const isFetchingOptions = ref(false);
const currentStep = ref(STEP_SELECT);
const topupOptions = ref([]);

const selectedOption = computed(() => {
  return topupOptions.value.find(
    option => option.id === selectedProductId.value
  );
});

const formattedAmount = computed(() => {
  if (!selectedOption.value) return '';
  const { prefix, suffix, price } = selectedOption.value;
  return `${prefix || ''}${price}${suffix ? ` ${suffix}` : ''}`.trim();
});

const selectedProductName = computed(() => {
  if (!selectedOption.value) return '';
  return selectedOption.value.name;
});

const dialogTitle = computed(() => {
  return currentStep.value === STEP_SELECT
    ? t('BILLING_SETTINGS.TOPUP.MODAL_TITLE')
    : t('BILLING_SETTINGS.TOPUP.CONFIRM.TITLE');
});

const dialogDescription = computed(() => {
  return currentStep.value === STEP_SELECT
    ? t('BILLING_SETTINGS.TOPUP.MODAL_DESCRIPTION')
    : '';
});

const dialogWidth = computed(() => {
  return currentStep.value === STEP_SELECT ? 'xl' : 'md';
});

const handlePackageSelect = productId => {
  selectedProductId.value = productId;
};

const fetchTopupOptions = async () => {
  isFetchingOptions.value = true;
  try {
    const response = await EnterpriseAccountAPI.getTopupOptions();
    topupOptions.value = response.data.products || [];
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || t('BILLING_SETTINGS.TOPUP.FETCH_ERROR');
    useAlert(errorMessage);
    topupOptions.value = [];
  } finally {
    isFetchingOptions.value = false;
  }
};

const open = async () => {
  await fetchTopupOptions();
  selectedProductId.value = topupOptions.value[0]?.id || null;
  currentStep.value = STEP_SELECT;
  isLoading.value = false;
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleClose = () => {
  emit('close');
};

const goToConfirmStep = () => {
  if (!selectedOption.value) return;
  currentStep.value = STEP_CONFIRM;
};

const goBackToSelectStep = () => {
  currentStep.value = STEP_SELECT;
};

const buildRedirectPath = productUrl => {
  if (!productUrl) return null;

  try {
    const parsedUrl = new URL(productUrl);
    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch (_error) {
    return null;
  }
};

const handlePurchase = async () => {
  if (!selectedOption.value) return;

  isLoading.value = true;
  try {
    const redirectPath = buildRedirectPath(selectedOption.value.product_url);
    const payload = redirectPath ? { redirect_path: redirectPath } : {};
    const response = await EnterpriseAccountAPI.checkout(payload);
    const redirectUrl = response.data?.redirect_url;

    if (!redirectUrl) {
      throw new Error('Missing redirect URL');
    }

    window.location.assign(redirectUrl);
    close();
    emit('success', { redirect_url: redirectUrl });
    useAlert(t('BILLING_SETTINGS.TOPUP.REDIRECTING_TO_CHECKOUT'));
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || t('BILLING_SETTINGS.TOPUP.PURCHASE_ERROR');
    useAlert(errorMessage);
  } finally {
    isLoading.value = false;
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="dialogTitle"
    :description="dialogDescription"
    :width="dialogWidth"
    :show-confirm-button="false"
    :show-cancel-button="false"
    @close="handleClose"
  >
    <!-- Step 1: Select Credits Package -->
    <template v-if="currentStep === 'select'">
      <div v-if="isFetchingOptions" class="py-8 text-sm text-n-slate-11">
        {{ $t('BILLING_SETTINGS.TOPUP.LOADING') }}
      </div>
      <div v-else-if="!topupOptions.length" class="py-8 text-sm text-n-slate-11">
        {{ $t('BILLING_SETTINGS.TOPUP.NO_OPTIONS') }}
      </div>
      <div v-else class="grid grid-cols-1 gap-3">
        <label
          v-for="option in topupOptions"
          :key="option.id"
          class="relative flex flex-col p-4 border rounded-lg transition-all cursor-pointer bg-n-solid-1 hover:bg-n-solid-2"
          :class="[
            selectedProductId === option.id
              ? 'border-woot-500'
              : 'border-n-weak hover:border-n-strong',
          ]"
        >
          <input
            type="radio"
            name="whmcs-topup-product"
            class="sr-only"
            :value="option.id"
            :checked="selectedProductId === option.id"
            @change="handlePackageSelect(option.id)"
          />
          <span class="text-base font-medium text-n-slate-12">
            {{ option.name }}
          </span>
          <span class="mt-1 text-sm text-n-slate-11">
            {{ option.prefix }}{{ option.price }}
            <template v-if="option.suffix"> {{ option.suffix }} </template>
          </span>
          <span
            v-if="option.description"
            class="mt-2 text-xs text-n-slate-10 line-clamp-3"
          >
            {{ option.description }}
          </span>
        </label>
      </div>

      <div class="p-4 mt-6 rounded-lg bg-n-solid-2 border border-n-weak">
        <p class="text-sm text-n-slate-11">
          <span class="font-semibold text-n-slate-12">{{
            $t('BILLING_SETTINGS.TOPUP.NOTE_TITLE')
          }}</span>
          {{ $t('BILLING_SETTINGS.TOPUP.NOTE_DESCRIPTION') }}
        </p>
      </div>
    </template>

    <!-- Step 2: Confirm Purchase -->
    <template v-else>
      <div class="flex flex-col gap-4">
        <p class="text-sm text-n-slate-11">
          {{
            $t('BILLING_SETTINGS.TOPUP.CONFIRM.DESCRIPTION', {
              credits: selectedProductName,
              amount: formattedAmount,
            })
          }}
        </p>

        <div class="p-2.5 rounded-lg bg-n-amber-2 border border-n-amber-6">
          <p class="text-sm text-n-amber-11">
            {{ $t('BILLING_SETTINGS.TOPUP.CONFIRM.INSTANT_DEDUCTION_NOTE') }}
          </p>
        </div>
      </div>
    </template>

    <template #footer>
      <!-- Step 1 Footer -->
      <div
        v-if="currentStep === 'select'"
        class="flex items-center justify-between w-full gap-3"
      >
        <Button
          variant="faded"
          color="slate"
          :label="$t('BILLING_SETTINGS.TOPUP.CANCEL')"
          class="w-full"
          @click="close"
        />
        <Button
          color="blue"
          :label="$t('BILLING_SETTINGS.TOPUP.PURCHASE')"
          class="w-full"
          :disabled="!selectedProductId || isFetchingOptions"
          @click="goToConfirmStep"
        />
      </div>

      <!-- Step 2 Footer -->
      <div v-else class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="$t('BILLING_SETTINGS.TOPUP.CONFIRM.GO_BACK')"
          class="w-full"
          :disabled="isLoading"
          @click="goBackToSelectStep"
        />
        <Button
          color="blue"
          :label="$t('BILLING_SETTINGS.TOPUP.CONFIRM.CONFIRM_PURCHASE')"
          class="w-full"
          :is-loading="isLoading"
          @click="handlePurchase"
        />
      </div>
    </template>
  </Dialog>
</template>
